import React, { useEffect, useState } from 'react';
import api from '../api';
import {
  ArrowLeft, ShieldCheck, Warning, TrendUp, TrendDown, Minus,
  Lightning, Buildings, ChartLine, Scales, Target, ArrowsClockwise,
  CalendarBlank, User, FileText, Database, Eye
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

const SEVERITY_STYLES = {
  Critical: { bg: '#FDEDED', text: '#8B1A1A', border: '#C6493A' },
  High: { bg: '#FDEDED', text: '#C6493A', border: '#C6493A' },
  Medium: { bg: '#FFF4E5', text: '#D9943B', border: '#D9943B' },
  Low: { bg: '#E8F5EB', text: '#2D7D46', border: '#2D7D46' },
};

const URGENCY_MAP = {
  Critical: { bg: 'bg-[#8B1A1A]', text: 'text-white' },
  High: { bg: 'bg-status-alert', text: 'text-white' },
  Medium: { bg: 'bg-status-watch', text: 'text-white' },
  Low: { bg: 'bg-status-on-track', text: 'text-white' },
};

const TREND_ICON = {
  Deteriorating: { icon: TrendDown, color: 'text-status-alert', label: 'Deteriorating' },
  'Slightly Deteriorating': { icon: TrendDown, color: 'text-status-watch', label: 'Slightly Deteriorating' },
  Stable: { icon: Minus, color: 'text-content-secondary', label: 'Stable' },
  Improving: { icon: TrendUp, color: 'text-status-on-track', label: 'Improving' },
};

const OUTLOOK_COLORS = {
  Deteriorating: 'text-status-alert',
  'Slightly Deteriorating': 'text-status-watch',
  Stable: 'text-content-secondary',
  Improving: 'text-status-on-track',
};

const STATUS_STYLES = {
  Overdue: 'bg-status-alert-bg text-status-alert',
  Open: 'bg-status-watch-bg text-status-watch',
  'In Progress': 'bg-[#E0EDFF] text-[#2563EB]',
  Completed: 'bg-status-on-track-bg text-status-on-track',
};

const FRAMEWORK_STYLES = {
  NGFS: { bg: '#0A4D3C', text: '#FFFFFF' },
  'Basel Climate Risk': { bg: '#1E3A5F', text: '#FFFFFF' },
  'BSP ESRM': { bg: '#C2714F', text: '#FFFFFF' },
  DNSH: { bg: '#2D7D46', text: '#FFFFFF' },
  'PH Sustainable Finance Taxonomy': { bg: '#87A98E', text: '#1A1F1B' },
  TCFD: { bg: '#D9943B', text: '#1A1F1B' },
};

function ScoreGauge({ value, max = 100, label, size = 'md' }) {
  const pct = Math.min((value / max) * 100, 100);
  const getColor = () => {
    if (pct >= 75) return '#C6493A';
    if (pct >= 50) return '#D9943B';
    if (pct >= 25) return '#87A98E';
    return '#2D7D46';
  };
  const w = size === 'sm' ? 'w-16 h-16' : 'w-20 h-20';
  const r = size === 'sm' ? 24 : 30;
  const cx = size === 'sm' ? 32 : 40;
  const stroke = size === 'sm' ? 5 : 6;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference - (pct / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`relative ${w}`}>
        <svg className="w-full h-full -rotate-90" viewBox={`0 0 ${cx * 2} ${cx * 2}`}>
          <circle cx={cx} cy={cx} r={r} fill="none" stroke="#F1F3F5" strokeWidth={stroke} />
          <circle cx={cx} cy={cx} r={r} fill="none" stroke={getColor()} strokeWidth={stroke}
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            strokeLinecap="round" className="transition-all duration-700" />
        </svg>
        <span className={`absolute inset-0 flex items-center justify-center font-mono font-bold ${size === 'sm' ? 'text-xs' : 'text-sm'}`}
          style={{ color: getColor() }}>{value}</span>
      </div>
      <span className="text-[10px] text-content-secondary text-center leading-tight font-medium">{label}</span>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children, className = '' }) {
  return (
    <div className={`bg-white border border-border-default rounded-lg overflow-hidden ${className}`}>
      <div className="px-4 py-2.5 border-b border-border-subtle flex items-center gap-2">
        {Icon && <Icon size={14} className="text-brand-primary" weight="bold" />}
        <h3 className="font-heading font-semibold text-xs text-content-primary uppercase tracking-wider">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export default function BSFIProfileAnalysis({ bsfiId, onBack }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(`/api/supervisory/profile/${bsfiId}`)
      .then(r => { setProfile(r.data); setLoading(false); })
      .catch(e => { setError(e.response?.data?.detail || 'Failed to load'); setLoading(false); });
  }, [bsfiId]);

  if (loading) return (
    <div className="space-y-4 animate-pulse">
      <div className="h-8 bg-surface-muted rounded w-64" />
      <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-surface-muted rounded-lg" />)}</div>
    </div>
  );
  if (error) return (
    <div className="text-center py-12">
      <p className="text-status-alert mb-3">{error}</p>
      <button onClick={onBack} className="text-brand-primary text-sm hover:underline">Back to overview</button>
    </div>
  );
  if (!profile) return null;

  const p = profile;
  const trend = TREND_ICON[p.trend] || TREND_ICON.Stable;
  const TrendIcon = trend.icon;
  const urgency = URGENCY_MAP[p.urgency] || URGENCY_MAP.Medium;

  const radarData = [
    { metric: 'Physical', value: p.physical_risk_score },
    { metric: 'Transition', value: p.transition_risk_score },
    { metric: 'Nature', value: p.nature_risk_score },
    { metric: 'Vulnerability', value: p.vulnerability_score },
    { metric: 'Adapt. Cap.', value: 100 - (p.adaptation_capacity_score || 0) },
  ];

  const exposureChart = (p.sector_exposure || []).map(s => ({
    name: s.subsector?.length > 18 ? s.subsector.slice(0, 16) + '...' : s.subsector,
    value: s.pct,
    risk: s.risk_level,
  }));

  return (
    <div data-testid="bsfi-profile-analysis" className="space-y-4 animate-fade-in">
      {/* Back + Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button data-testid="profile-back-btn" onClick={onBack}
            className="mt-1 p-1.5 rounded-md hover:bg-surface-muted transition-colors text-content-secondary hover:text-brand-primary">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="font-heading font-light text-2xl text-content-primary tracking-tight">{p.name}</h1>
              <span className="font-mono text-xs bg-brand-primary/10 text-brand-primary px-2 py-0.5 rounded">{p.bsfi_id}</span>
              <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${urgency.bg} ${urgency.text}`}>{p.urgency} Urgency</span>
            </div>
            <p className="text-xs text-content-secondary mt-0.5">
              {p.bank_type} &middot; {p.region} &middot; {p.primary_exposure}
              <span className="mx-1.5">|</span>
              ESRM: <span className="font-semibold">{p.esrm_maturity}</span>
              <span className="mx-1.5">|</span>
              Remediation: <span className={`font-semibold ${p.remediation_status === 'Open' ? 'text-status-watch' : p.remediation_status === 'Completed' ? 'text-status-on-track' : 'text-status-alert'}`}>{p.remediation_status}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <p className="text-[10px] text-content-disabled uppercase tracking-wider font-medium">Composite Score</p>
            <p className="font-mono font-bold text-2xl text-brand-primary">{p.composite_score}</p>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-surface-muted">
            <TrendIcon size={14} className={trend.color} weight="bold" />
            <span className={`text-xs font-semibold ${trend.color}`}>{trend.label}</span>
          </div>
        </div>
      </div>

      {/* Risk Standards & Frameworks */}
      <div data-testid="frameworks-badges" className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] text-content-disabled font-semibold uppercase tracking-wider mr-1">Risk Standards:</span>
        {(p.frameworks || []).map(fw => {
          const style = FRAMEWORK_STYLES[fw] || { bg: '#E9ECEF', text: '#49564C' };
          return (
            <span key={fw} data-testid={`framework-badge-${fw.replace(/\s+/g, '-').toLowerCase()}`}
              className="text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wide transition-transform hover:scale-105"
              style={{ backgroundColor: style.bg, color: style.text }}>
              {fw}
            </span>
          );
        })}
        <span className="ml-auto text-[10px] text-content-disabled">
          Data Quality: <span className={`font-bold ${p.data_quality === 'Excellent' ? 'text-status-on-track' : p.data_quality === 'Good' ? 'text-brand-primary' : p.data_quality === 'Fair' ? 'text-status-watch' : 'text-status-alert'}`}>{p.data_quality}</span>
          &nbsp;|&nbsp;Confidence: <span className="font-mono font-bold text-brand-primary">{p.confidence_score}%</span>
        </span>
      </div>

      {/* Score Gauges + Radar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-xs text-content-primary uppercase tracking-wider mb-3">Risk Scores</h3>
          <div className="grid grid-cols-3 gap-2">
            <ScoreGauge value={p.physical_risk_score} label="Physical" />
            <ScoreGauge value={p.transition_risk_score} label="Transition" />
            <ScoreGauge value={p.nature_risk_score} label="Nature" />
          </div>
          <div className="border-t border-border-subtle mt-3 pt-3 grid grid-cols-3 gap-2">
            <ScoreGauge value={p.vulnerability_score} label="Vulnerability" size="sm" />
            <ScoreGauge value={p.adaptation_capacity_score} label="Adaptation" size="sm" />
            <ScoreGauge value={p.confidence_score} label="Confidence" size="sm" />
          </div>
        </div>

        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-xs text-content-primary uppercase tracking-wider mb-2">Risk Radar</h3>
          <ResponsiveContainer width="100%" height={200}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#F1F3F5" />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 9, fill: '#49564C' }} />
              <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 8 }} />
              <Radar dataKey="value" stroke="#C2714F" fill="#C2714F" fillOpacity={0.2} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-border-default rounded-lg p-4">
          <h3 className="font-heading font-semibold text-xs text-content-primary uppercase tracking-wider mb-2">Sector Exposure Mix</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={exposureChart} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
              <XAxis type="number" tick={{ fontSize: 9 }} domain={[0, 'dataMax + 5']} unit="%" />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#49564C' }} width={100} />
              <Tooltip formatter={v => [`${v}%`]} />
              <Bar dataKey="value" radius={[0, 3, 3, 0]}>
                {exposureChart.map((e, i) => (
                  <Cell key={i} fill={e.risk === 'Critical' ? '#8B1A1A' : e.risk === 'High' ? '#C6493A' : e.risk === 'Medium' ? '#D9943B' : '#2D7D46'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sector Exposure Table */}
      <SectionCard title="Sector Exposure Detail" icon={Buildings}>
        <div className="overflow-x-auto">
          <table data-testid="sector-exposure-table" className="w-full text-[11px]">
            <thead><tr className="bg-surface-muted">
              {['Sector', 'Subsector', 'Share', 'Exposure (₱Bn)', 'Risk Level', 'Top Obligors'].map(h => (
                <th key={h} className="text-left px-3 py-2 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-border-subtle">
              {(p.sector_exposure || []).map((s, i) => (
                <tr key={i} className="hover:bg-surface-muted/50 transition-colors">
                  <td className="px-3 py-2 font-medium">{s.sector}</td>
                  <td className="px-3 py-2">{s.subsector}</td>
                  <td className="px-3 py-2 font-mono font-semibold">{s.pct}%</td>
                  <td className="px-3 py-2 font-mono">₱{s.exposure_bn}</td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                      style={{ backgroundColor: `${(SEVERITY_STYLES[s.risk_level] || SEVERITY_STYLES.Medium).bg}`, color: (SEVERITY_STYLES[s.risk_level] || SEVERITY_STYLES.Medium).text }}>
                      {s.risk_level}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono">{s.top_obligors}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Risk Transmission Channels */}
      <SectionCard title="Risk Transmission Channels" icon={Lightning}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(p.risk_transmission || []).map((rt, i) => {
            const sev = SEVERITY_STYLES[rt.severity] || SEVERITY_STYLES.Medium;
            return (
              <div key={i} data-testid={`risk-channel-${i}`}
                className="border rounded-lg p-3 transition-all hover:shadow-sm"
                style={{ borderColor: sev.border + '40' }}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-heading font-semibold text-xs text-content-primary">{rt.channel}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: sev.bg, color: sev.text }}>{rt.severity}</span>
                </div>
                <p className="text-[11px] text-content-secondary leading-relaxed mb-2">{rt.description}</p>
                <div className="flex items-center gap-1">
                  <span className="font-mono font-bold text-sm" style={{ color: sev.text }}>{rt.impact_bps}</span>
                  <span className="text-[10px] text-content-disabled">bps impact</span>
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Forecast + Scenarios side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Forecast */}
        <SectionCard title="Forward-Looking Assessment" icon={ChartLine}>
          {p.forecast && (
            <div className="space-y-3">
              <div className="flex gap-3">
                {[
                  { label: '3-Month', val: p.forecast['3m_outlook'] },
                  { label: '6-Month', val: p.forecast['6m_outlook'] },
                  { label: '12-Month', val: p.forecast['12m_outlook'] },
                ].map((o, i) => (
                  <div key={i} className="flex-1 bg-surface-muted rounded-lg p-2.5 text-center">
                    <p className="text-[10px] text-content-disabled font-medium">{o.label}</p>
                    <p className={`text-xs font-bold mt-0.5 ${OUTLOOK_COLORS[o.val] || 'text-content-primary'}`}>{o.val}</p>
                  </div>
                ))}
              </div>
              <div className="bg-surface-muted/50 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-content-secondary uppercase tracking-wider mb-1">Expected Stress Range</p>
                <p className="font-mono font-bold text-sm text-status-alert">{p.forecast.expected_range_bps} bps</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-content-secondary uppercase tracking-wider mb-1.5">Key Drivers</p>
                <ul className="space-y-1">
                  {(p.forecast.drivers || []).map((d, i) => (
                    <li key={i} className="text-[11px] text-content-secondary flex items-start gap-1.5">
                      <span className="text-brand-accent mt-0.5 shrink-0">&bull;</span>{d}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-status-alert uppercase tracking-wider mb-1.5">Downside Triggers</p>
                <ul className="space-y-1">
                  {(p.forecast.downside_triggers || []).map((t, i) => (
                    <li key={i} className="text-[11px] text-content-secondary flex items-start gap-1.5">
                      <Warning size={10} className="text-status-alert mt-0.5 shrink-0" weight="bold" />{t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </SectionCard>

        {/* Scenario Analysis */}
        <SectionCard title="Scenario Analysis" icon={Scales}>
          <div className="overflow-x-auto">
            <table data-testid="scenario-table" className="w-full text-[11px]">
              <thead><tr className="bg-surface-muted">
                {['Scenario', 'NPL', 'Collateral', 'Earnings', 'Capital', 'Recovery'].map(h => (
                  <th key={h} className="text-left px-2.5 py-2 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>
                ))}
              </tr></thead>
              <tbody className="divide-y divide-border-subtle">
                {(p.scenarios || []).map((s, i) => {
                  const isWorst = i === (p.scenarios || []).length - 1;
                  return (
                    <tr key={i} className={`transition-colors ${isWorst ? 'bg-status-alert-bg/30' : 'hover:bg-surface-muted/50'}`}>
                      <td className={`px-2.5 py-2 font-medium ${isWorst ? 'text-status-alert font-semibold' : ''}`}>{s.name}</td>
                      <td className="px-2.5 py-2 font-mono font-semibold text-status-alert">{s.npl_migration}</td>
                      <td className="px-2.5 py-2 font-mono text-status-alert">{s.collateral_haircut}</td>
                      <td className="px-2.5 py-2 font-mono font-semibold text-status-alert">{s.earnings_shock}</td>
                      <td className="px-2.5 py-2 font-mono text-status-alert">{s.capital_impact}</td>
                      <td className="px-2.5 py-2 font-mono text-content-secondary">{s.recovery_lag}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      {/* Evidence & Data Quality */}
      <SectionCard title="Evidence & Data Quality" icon={Database}>
        {p.evidence && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                { label: 'Supporting Docs', val: p.evidence.supporting_docs, color: 'text-brand-primary' },
                { label: 'Stale Warnings', val: p.evidence.stale_data_warnings, color: p.evidence.stale_data_warnings > 2 ? 'text-status-alert' : 'text-status-watch' },
                { label: 'Missing ESG', val: p.evidence.missing_esg_info, color: p.evidence.missing_esg_info > 3 ? 'text-status-alert' : 'text-status-watch' },
                { label: 'Unresolved', val: p.evidence.unresolved_ambiguity, color: p.evidence.unresolved_ambiguity > 0 ? 'text-status-watch' : 'text-status-on-track' },
                { label: 'Contradictions', val: p.evidence.contradictory_flags, color: p.evidence.contradictory_flags > 0 ? 'text-status-alert' : 'text-status-on-track' },
              ].map((m, i) => (
                <div key={i} className="bg-surface-muted rounded-lg p-2.5 text-center">
                  <p className={`font-mono font-bold text-lg ${m.color}`}>{m.val}</p>
                  <p className="text-[10px] text-content-disabled font-medium">{m.label}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="text-[10px] font-semibold text-content-secondary uppercase tracking-wider mb-1.5">Key Assessment Rationale</p>
              <ul className="space-y-1">
                {(p.evidence.top_reasons || []).map((r, i) => (
                  <li key={i} className="text-[11px] text-content-secondary flex items-start gap-1.5">
                    <span className="text-brand-accent mt-0.5 shrink-0">{i + 1}.</span>{r}
                  </li>
                ))}
              </ul>
            </div>
            {p.evidence.what_changed && (
              <div className="bg-status-watch-bg/50 border border-status-watch/20 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-status-watch uppercase tracking-wider mb-1">What Changed</p>
                <p className="text-[11px] text-content-secondary">{p.evidence.what_changed}</p>
              </div>
            )}
            {(p.evidence.missing_data || []).length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-status-alert uppercase tracking-wider mb-1.5">Missing Data Gaps</p>
                <div className="flex flex-wrap gap-1.5">
                  {p.evidence.missing_data.map((d, i) => (
                    <span key={i} className="text-[10px] bg-status-alert-bg text-status-alert px-2 py-0.5 rounded-full font-medium">{d}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Supervisory Action Panel */}
      <SectionCard title="Supervisory Action Panel" icon={Target}>
        <div className="overflow-x-auto">
          <table data-testid="action-panel-table" className="w-full text-[11px]">
            <thead><tr className="bg-surface-muted">
              {['Action', 'Owner', 'Due Date', 'Severity', 'Status', 'Aging'].map(h => (
                <th key={h} className="text-left px-3 py-2 font-semibold text-content-secondary text-[10px] uppercase tracking-wider">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-border-subtle">
              {(p.actions || []).map((a, i) => (
                <tr key={i} className="hover:bg-surface-muted/50 transition-colors">
                  <td className="px-3 py-2.5 font-medium max-w-[280px]">{a.action}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className="flex items-center gap-1"><User size={10} className="text-content-disabled" />{a.owner}</span>
                  </td>
                  <td className="px-3 py-2.5 font-mono whitespace-nowrap">
                    <span className="flex items-center gap-1"><CalendarBlank size={10} className="text-content-disabled" />{a.due_date}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                      style={{ backgroundColor: (SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.Medium).bg, color: (SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.Medium).text }}>
                      {a.severity}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLES[a.status] || 'bg-surface-muted text-content-secondary'}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono">
                    {a.aging_days > 0 ? <span className="text-status-alert font-semibold">{a.aging_days}d</span> : <span className="text-content-disabled">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
